import { Col, List, Row } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  AdvancedSetting,
  SettingType,
} from 'api/advanced-settings/settings-types';
import { ProjectStatus } from 'api/project';
import { ReactComponent as ScheduledNotificationsIcon } from 'assets/svgs/scheduled-notifications.svg';
import { Checkbox } from 'components/atoms/checkbox';
import { StatusPickerOption } from 'components/atoms/status-picker/option';
import { MultiSelectEditor } from 'components/organisms/tasks/criteria/multi-select-editor';
import { FieldType, MultiSelectField } from 'components/organisms/tasks/fields';
import { SelectFilterOperationType } from 'components/organisms/tasks/filters/select';
import { TagTriggerOption } from 'components/organisms/template-insight/template-filter';
import { useTranslation } from 'hooks/use-translation';
import { byFieldName } from 'utils/by-field-name';
import { useProjectCustomFields } from 'utils/project-fields/use-project-fields';
import { Content, ListItem } from '../common';
import { SettingHeader } from '../common/setting-header';

const EmptySpace = styled.div``;

const STOP_SCHEDULED_NOTIFICATIONS_IF_PROJECT_STATUS_MATCHES_DEFAULT = [
  ProjectStatus.Completed,
  ProjectStatus.Cancelled,
  ProjectStatus.OnHold,
];

export const ScheduledNotifications = ({
  settingsConfig,
  onSettingChange,
}: {
  settingsConfig: AdvancedSetting[];
  onSettingChange: (value: AdvancedSetting) => void;
}) => {
  const title = useTranslation(
    'settings.advanced.scheduled_notifications.title',
  );

  const { data: fields } = useProjectCustomFields();
  const projectStatusField = fields?.find((field) => {
    return byFieldName(field, 'status');
  });

  const config = settingsConfig?.find(
    (setting) => setting.eventType === SettingType.StopScheduledNotifications,
  );

  if (!config) {
    return null;
  }

  const updateConfig = (
    eventValue: boolean,
    statuses: number[],
    isTogglingFeature = false,
  ) => {
    if (statuses.length === 0) {
      onSettingChange({
        eventType: SettingType.StopScheduledNotifications,
        eventValue: isTogglingFeature ? eventValue : false, // Automatically disable the feature
        projectWithStatusesNotification: isTogglingFeature
          ? STOP_SCHEDULED_NOTIFICATIONS_IF_PROJECT_STATUS_MATCHES_DEFAULT // Automatically suggest default statuses
          : statuses,
      });
    } else {
      onSettingChange({
        eventType: SettingType.StopScheduledNotifications,
        eventValue,
        projectWithStatusesNotification: statuses,
      });
    }
  };

  const projectStatuses = projectStatusField?.metaData?.options
    ?.filter((option) => option.active)
    .sort((a, b) => (a?.position || 1) - (b?.position || 1));

  return (
    <>
      <SettingHeader title={title} icon={<ScheduledNotificationsIcon />} />
      <List
        dataSource={[config]}
        renderItem={({
          eventType,
          eventValue,
          projectWithStatusesNotification,
        }) => (
          <ListItem title={title}>
            <Row align="middle">
              <Col flex="16px">
                <Checkbox
                  value={eventType}
                  checked={eventValue}
                  onChange={(event) =>
                    updateConfig(
                      event.target.checked,
                      projectWithStatusesNotification ?? [],
                      true,
                    )
                  }
                />
              </Col>
              <Col flex="calc(100% - 32px)">
                <Content>
                  <FormattedMessage id="settings.advanced.stop_schedule_notification_project_statuses" />
                </Content>
              </Col>
            </Row>
            <Row align="middle" hidden={!eventValue}>
              <Col flex="32px">
                <EmptySpace />
              </Col>
              <Col flex="calc(100% - 32px)">
                <MultiSelectEditor
                  type="tag"
                  showDoneButton
                  field={
                    {
                      ...projectStatusField,
                      type: FieldType.MultiSelect,
                    } as MultiSelectField
                  }
                  filterType={SelectFilterOperationType.IsOneOf}
                  components={{
                    SingleValue: StatusPickerOption,
                    TriggerOption: TagTriggerOption,
                  }}
                  options={projectStatuses ?? []}
                  value={projectWithStatusesNotification ?? []}
                  onChange={(statuses) => updateConfig(eventValue, statuses)}
                />
              </Col>
            </Row>
          </ListItem>
        )}
      />
    </>
  );
};
